import { getObjectKeys } from '@vestico/utils';

import { ConditionOperator, ConditionsType, DocumentID } from '../common';
import { ProductBase, ProductGender } from '../products/product';
import { getSizes } from '../products/variants';
import { SupportedMatchInfoHashCategoryDefault } from '../userMatchInfoHash';
import { VisibilityLevel } from '../visibilityLevel';
import { ProviderDocID } from './provider';

export const SupportedSizeChartCategoryDefault = SupportedMatchInfoHashCategoryDefault;
export const SupportedSizeChartCategories = [
  'default',
  'bras',
  'coats_and_jackets',
  'dresses_and_jumpsuits',
  'jeans',
  'shirts',
  'shorts',
  'skirts',
  'suits',
  'sweatshirts_and_hoodies',
  'swimwear',
  'trousers',
  'tshirts',
  'leggings',
  'shoes',
  'accessories',
] as const;
export type SupportedSizeChartCategory = (typeof SupportedSizeChartCategories)[number];

export interface SizesChartSize {
  min: number;
  max: number;
}

// NOTE: The order decides how they're displayed in the UI
export const SizeChartMeasurementOptions = ['chest', 'waist', 'hip', 'height', 'inseam', 'armLength', 'underbust', 'braCupDifference'] as const;
export type SizeChartMeasurementOption = (typeof SizeChartMeasurementOptions)[number];
export type FlexibleSizeChartMeasurementOption = SizeChartMeasurementOption | string;
export const DefaultSizeChartMeasurementOptions: SizeChartMeasurementOption[] = ['chest', 'waist', 'hip'];
export const TransformableGarmentMeasurementTypes: SizeChartMeasurementOption[] = DefaultSizeChartMeasurementOptions;

export interface SizeChartItem extends Partial<Record<SizeChartMeasurementOption, SizesChartSize>> {
  sizeValues: string[];
  [x: string]: SizesChartSize | string[] | undefined;
}

export type SizeChartDocID = string;
export const SizeChartDocIDNew = 'new';

export const InputUnits = ['cm', 'in'] as const;
export type InputUnit = (typeof InputUnits)[number];

export const MeasurementTypes = ['garment', 'body'] as const;
export type MeasurementType = (typeof MeasurementTypes)[number];

export type GarmentEase = number;

export interface SizeChartItemsConfig {
  isRange: boolean;
  displayInStore: boolean;
  measurementType: MeasurementType;
  items: SizeChartItem[];
  description?: string;
}

export interface AdditionalSizeChartOption extends SizeChartItemsConfig {
  productAttribute: string;
}

export const ConditionOptions = ['all', 'tags', 'brands', 'collections', 'product types', 'product categories', 'products'] as const;
export type ConditionOption = (typeof ConditionOptions)[number];

export interface Condition {
  option: ConditionOption;
  operator: ConditionOperator;
  values: DocumentID[];
}

export interface ConditionsFilter {
  type: ConditionsType;
  conditions: Condition[];
}

export interface SizeChart {
  documentID: SizeChartDocID;
  provider: ProviderDocID;
  dateCreated: Date;
  name: string;
  garmentType: SupportedSizeChartCategory;
  garmentEase: GarmentEase | undefined;
  gender: ProductGender;
  products: number;
  unit: InputUnit;
  unmatched?: boolean;
  itemsConfig: SizeChartItemsConfig;
  additionalOptions: AdditionalSizeChartOption[];
  productsFilter: ConditionsFilter;
  customDisclaimer?: string;
}

export const SizeNotEntered = -1;

const isCompletedSizeChartSize = (size?: SizesChartSize) => {
  if (!size) {
    return false;
  }
  const { min, max } = size;
  return !!min && !!max && min !== SizeNotEntered && max !== SizeNotEntered;
};

const isValidSizeChartSize = (size?: SizesChartSize) => {
  if (!size) {
    return true;
  }
  const { min, max } = size;
  return min && max && min <= max;
};

export const isCompletedSizeChartItem = (item: SizeChartItem, sanitize?: boolean) => {
  const { sizeValues } = item;

  const sanitizedSizeValues = sizeValues.filter((sizeValue) => sizeValue && sizeValue.length > 0);

  if (sanitize) {
    item.sizeValues = sanitizedSizeValues;
  }

  if (sanitizedSizeValues.length === 0) {
    return false;
  }

  const completedSizes = getObjectKeys(item)
    .map((option) => item[option])
    .filter((s) => !Array.isArray(s) && isCompletedSizeChartSize(s));
  return completedSizes.length > 0;
};

const isValidSizeChartItem = (item: SizeChartItem) => {
  const validSizes = SizeChartMeasurementOptions.map((option) => item[option]).filter((s) => isValidSizeChartSize(s));
  return validSizes.length === SizeChartMeasurementOptions.length;
};

export const isEmptySizeChart = (sizeChart: SizeChart) => {
  const { name, gender, itemsConfig, garmentType } = sizeChart;
  const { items } = itemsConfig;
  const hasData = name.length > 0 || gender !== ProductGender.unknown || !!garmentType;
  if (!hasData) {
    return false;
  }

  const completedItems = items.filter((item) => isCompletedSizeChartItem(item));
  return completedItems.length === 0;
};

export const isValidSizeChart = (sizeChart: SizeChart) => {
  if (isEmptySizeChart(sizeChart)) {
    return false;
  }
  const completedItems = sizeChart.itemsConfig.items.filter((item) => isCompletedSizeChartItem(item));
  const validItems = completedItems.filter((item) => isValidSizeChartItem(item));
  return validItems.length === completedItems.length;
};

export const sanitizeSizeChartOptionValues = (sizeChart: SizeChart) => {
  for (const item of sizeChart.itemsConfig.items) {
    for (const option of SizeChartMeasurementOptions) {
      const isCompleted = isCompletedSizeChartSize(item[option]);
      // Remove non-completed options
      if (!isCompleted) {
        // skipcq JS-0320
        delete item[option];
      }
    }
  }

  for (const additionalChart of sizeChart.additionalOptions) {
    for (const item of additionalChart.items) {
      for (const option of SizeChartMeasurementOptions) {
        const isCompleted = isCompletedSizeChartSize(item[option]);
        // Remove non-completed options
        if (!isCompleted) {
          // skipcq JS-0320
          delete item[option];
        }
      }
    }
  }
};

export const getSizeChartMatchesProductSizes = (productSizes: Set<string>, sizeChart: SizeChart) => {
  const { items } = sizeChart.itemsConfig;

  let sizeChartMatchesProductSize = false;
  for (const sizeChartItem of items) {
    for (const sizeValue of sizeChartItem.sizeValues) {
      sizeChartMatchesProductSize = productSizes.has(sizeValue.toLowerCase());

      if (sizeChartMatchesProductSize) {
        break;
      }
    }

    if (sizeChartMatchesProductSize) {
      break;
    }
  }

  return sizeChartMatchesProductSize;
};

// SIZE CHART MAPPING WEIGHTS

export type SizeChartCategoryWeights = Record<SizeChartMeasurementOption, number>;

const SIZE_CHART_WEIGHTS_DEFAULT: SizeChartCategoryWeights = {
  chest: 0.33,
  waist: 0.5,
  hip: 0.66,
  inseam: 0.2,
  height: 0.2,
  armLength: 0.2,
  braCupDifference: 0.2,
  underbust: 0.33,
};

const SIZE_CHART_WEIGHTS_BRAS: SizeChartCategoryWeights = {
  chest: 0.9,
  waist: 0.3,
  hip: 0.1,
  inseam: 0.0,
  height: 0.0,
  armLength: 0.0,
  braCupDifference: 0.9,
  underbust: 0.9,
};

const SIZE_CHART_WEIGHTS_UPPER: SizeChartCategoryWeights = {
  chest: 0.7,
  waist: 0.5,
  hip: 0.2,
  inseam: 0.0,
  height: 0.2,
  armLength: 0.3,
  braCupDifference: 0.5,
  underbust: 0.5,
};

const SIZE_CHART_WEIGHTS_COATS: SizeChartCategoryWeights = {
  chest: 0.7,
  waist: 0.4,
  hip: 0.3,
  inseam: 0.0,
  height: 0.0,
  armLength: 0.2,
  braCupDifference: 0.2,
  underbust: 0.7,
};

const SIZE_CHART_WEIGHTS_DRESSES: SizeChartCategoryWeights = {
  chest: 0.5,
  waist: 0.5,
  hip: 0.5,
  inseam: 0.0,
  height: 0.2,
  armLength: 0.0,
  braCupDifference: 0.4,
  underbust: 0.5,
};

const SIZE_CHART_WEIGHTS_BOTTOMS: SizeChartCategoryWeights = {
  chest: 0.1,
  waist: 0.6,
  hip: 0.6,
  inseam: 0.3,
  height: 0.3,
  armLength: 0.0,
  braCupDifference: 0.0,
  underbust: 0.0,
};

const SIZE_CHART_WEIGHTS_UNMATCHED: SizeChartCategoryWeights = {
  chest: 0.0,
  waist: 0.0,
  hip: 0.0,
  inseam: 0.0,
  height: 0.0,
  armLength: 0.0,
  braCupDifference: 0.0,
  underbust: 0.0,
};

export const SIZE_CHART_WEIGHTS_BY_CATEGORY: { [key in SupportedSizeChartCategory]: SizeChartCategoryWeights } = {
  default: SIZE_CHART_WEIGHTS_DEFAULT,
  bras: SIZE_CHART_WEIGHTS_BRAS,
  coats_and_jackets: SIZE_CHART_WEIGHTS_COATS,
  dresses_and_jumpsuits: SIZE_CHART_WEIGHTS_DRESSES,
  jeans: SIZE_CHART_WEIGHTS_BOTTOMS,
  shirts: SIZE_CHART_WEIGHTS_UPPER,
  shorts: SIZE_CHART_WEIGHTS_BOTTOMS,
  skirts: SIZE_CHART_WEIGHTS_BOTTOMS,
  suits: SIZE_CHART_WEIGHTS_DRESSES,
  sweatshirts_and_hoodies: SIZE_CHART_WEIGHTS_UPPER,
  swimwear: SIZE_CHART_WEIGHTS_DEFAULT,
  trousers: SIZE_CHART_WEIGHTS_BOTTOMS,
  leggings: SIZE_CHART_WEIGHTS_BOTTOMS,
  tshirts: SIZE_CHART_WEIGHTS_UPPER,
  accessories: SIZE_CHART_WEIGHTS_UNMATCHED,
  shoes: SIZE_CHART_WEIGHTS_UNMATCHED,
};

export const sizeRecommendationAvailableForProduct = (product: ProductBase, allProductSizeCharts: SizeChart[]) => {
  const { visibility } = product;
  const productSizes = new Set(getSizes(product, true));

  const isVisible = visibility <= VisibilityLevel.hiddenDetection;
  const filteredSizeCharts = allProductSizeCharts.filter((sizeChart) => {
    return sizeChart && !sizeChart.unmatched ? getSizeChartMatchesProductSizes(productSizes, sizeChart) : false;
  });

  return isVisible && filteredSizeCharts.length > 0;
};

const unmatchedTypes: SupportedSizeChartCategory[] = ['shoes', 'accessories'];
export const isUnmatchedGarmentType = (type: SupportedSizeChartCategory) => {
  return unmatchedTypes.includes(type);
};

const defaultEaseByGarmentType: { [key in SupportedSizeChartCategory]: number } = {
  accessories: 0,
  bras: -2.5,
  coats_and_jackets: 10,
  default: 0,
  dresses_and_jumpsuits: 5,
  jeans: 5,
  leggings: -25,
  shirts: 10,
  shoes: 0,
  shorts: 5,
  skirts: 5,
  suits: 5,
  sweatshirts_and_hoodies: 10,
  swimwear: 0,
  trousers: 5,
  tshirts: 10,
};

export const defaultEaseForGarmentType = (type: SupportedSizeChartCategory) => {
  return defaultEaseByGarmentType[type];
};

export const calculateBodyMeasurementsForValue = (value: number, ease: GarmentEase | undefined) => {
  return value * 2 - (ease ?? 0);
};

export const calculateBodyMeasurementsForConfig = (itemsConfig: SizeChartItemsConfig, ease: GarmentEase | undefined) => {
  const { items, measurementType } = itemsConfig;
  if (measurementType === 'body') {
    return items;
  }

  return items.map((item) => {
    const nItem = { ...item };
    for (const key of getObjectKeys(nItem)) {
      const value = nItem[key];
      if (!value || Array.isArray(value) || !TransformableGarmentMeasurementTypes.includes(key as SizeChartMeasurementOption)) {
        continue;
      }

      const { min, max } = value;
      const newSize: SizesChartSize = {
        min: calculateBodyMeasurementsForValue(min, ease),
        max: calculateBodyMeasurementsForValue(max, ease),
      };
      nItem[key] = newSize;
    }
    return nItem;
  });
};

export const collectProductAttributesForSizeCharts = (sizeCharts: SizeChart[]) => {
  const attributes = new Set<string>();

  for (const sizeChart of sizeCharts) {
    for (const additionalOption of sizeChart.additionalOptions) {
      attributes.add(additionalOption.productAttribute);
    }
  }
  return [...attributes];
};
